import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  InputBase,
  List,
  ListItem,
  ListItemText,
  Paper,
  Rating,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useJsApiLoader } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import { setSearchKeys } from "../../lib/redux/slices/searchKeysSlice";
import { fetchWorker } from "../../lib/redux/slices/workerSlice";
import { t } from "i18next";

const libraries = ["places"];

const SearchBar = () => {
  const searchBarRef = useRef(null);
  const dateTimeRef = useRef(null);
  const dispatch = useDispatch();
  const [activeBox, setActiveBox] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const subServiceId = useSelector(
    (state) => state.activeSubServiceIdReducer.subServiceId
  );

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target)
      ) {
        setActiveBox(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (event) => {
    const input = event.target.value;
    dispatch(
      setSearchKeys({ ...searchKeys, workerLocation: { addres: input } })
    );

    if (!input.trim() || !isLoaded) {
      setSuggestions([]);
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSuggestions(predictions || []);
      }
    });
  };

  const handleSuggestionClick = (suggestion) => {
    const placeId = suggestion.place_id;

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    service.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        dispatch(
          setSearchKeys({
            ...searchKeys,
            workerLocation: {
              addres: suggestion.description,
              latitude,
              longitude,
            },
          })
        );
        setSuggestions([]);
      } else {
        console.error("Error fetching place details:", status);
      }
    });
  };

  const handleSearchWorker = (e) => {
    e.preventDefault();

    let lat = searchKeys?.workerLocation?.latitude || "";
    let lng = searchKeys?.workerLocation?.longitude || "";

    if (
      !searchKeys?.workerName &&
      !lat &&
      !lng &&
      searchKeys?.workerRating <= 0 &&
      searchKeys?.workerDateTime == null
    )
      return;

    const query = {
      serviceId: subServiceId,
      lat: lat,
      lng: lng,
      name: searchKeys?.workerName || "",
      rating: searchKeys?.workerRating || "",
      date: searchKeys.workerDateTime || "",
    };

    dispatch(fetchWorker(query));
  };

  const style = {
    mainBox: {
      display: "flex",
      alignItems: "center",
      borderRadius: 32,
      maxWidth: 900,
      width: "100%",
      mx: 2,
      border: "1px solid #DDDDDD",
      boxShadow: "0 3px 12px 0 rgba(0,0,0,0.1),0 1px 2px 0 rgba(0,0,0,0.08)",
      gap: 1,
      transition: "all 0.3s ease",
      bgcolor: activeBox !== null ? "#EBEBEB" : "white",
      "&:hover": { boxShadow: 2 },
      "@media (max-width: 600px)": {
        display: "none",
        overflowX: "auto",
        borderRadius: 25,
        gap: 0.5,
        // minWidth: "900px",
        // marginLeft: "400px",
      },
    },
    childBox1: {
      height: "100%",
      borderRadius: "32px",
      boxShadow: activeBox === 0 ? 4 : 0,
      flex: 1,
      paddingLeft: 4,
      // py: 0.6,
      borderColor: "divider",
      transition: "all 0.3s ease",
      cursor: "pointer",
      bgcolor: "white",
      "&:hover": {
        bgcolor: activeBox === 0 ? "white" : "grey.100",
      },
      "&:not(:hover)": {
        bgcolor: activeBox !== null && activeBox !== 0 ? "#EBEBEB" : "white",
      },
      "@media (max-width: 600px)": {
        paddingLeft: 2,
        py: 0.4,
      },
    },
    childBox1Sibling: {
      paddingTop: "5px",
      height: "100%",

      borderRight: activeBox === null ? "1px solid #EBEBEB" : "none",
      width: "90%",
      "&:hover": { borderRight: "none" },
      "@media (max-width: 600px)": {
        width: "100%",
      },
    },
    childBox2: {
      height: "100%",
      borderRadius: "32px",
      boxShadow: activeBox === 1 ? 4 : 0,
      flex: 1,
      paddingLeft: 2,
      // py: 0.8,
      borderColor: "divider",
      transition: "all 0.3s ease",
      cursor: "pointer",
      bgcolor: "white",
      position: "relative",
      "&:hover": {
        bgcolor: activeBox === 1 ? "white" : "grey.100",
      },
      "&:not(:hover)": {
        bgcolor: activeBox !== null && activeBox !== 1 ? "#EBEBEB" : "white",
      },
      "@media (max-width: 600px)": {
        paddingLeft: 1,
        py: 0.4,
      },
    },
    childBox2Sibling: {
      paddingTop: "5px",
      height: "100%",
      borderRight: activeBox === null ? "1px solid #EBEBEB" : "none",
      width: "90%",
      "&:hover": { borderRight: "none" },
      "@media (max-width: 600px)": {
        width: "100%",
      },
    },
    suggestedLocationMenu: {
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      zIndex: 999,
      maxHeight: 400,
      overflowY: "auto",
      mt: 0.5,
      borderRadius: 2,
      width: "400px",
    },
    childBox3: {
      height: "100%",
      borderRadius: "32px",
      boxShadow: activeBox === 2 ? 4 : 0,
      flex: 0.8,
      paddingLeft: 2,
      // py: "8px  ",
      borderColor: "divider",
      transition: "all 0.3s ease",
      cursor: "pointer",
      bgcolor: "white",
      "&:hover": {
        bgcolor: activeBox === 2 ? "white" : "grey.100",
      },
      "&:not(:hover)": {
        bgcolor: activeBox !== null && activeBox !== 2 ? "#EBEBEB" : "white",
      },
      "@media (max-width: 600px)": {
        paddingLeft: 1,
        py: 0.4,
      },
    },
    childBox3Sibling: {
      height: "100%",
      paddingTop: "5px",
      borderRight: activeBox === null ? "1px solid #EBEBEB" : "none",
      width: "90%",
      "&:hover": { borderRight: "none" },
    },
    childBox4: {
      display: "flex",
      justifyContent: "space-between",
      borderRadius: "32px",
      boxShadow: activeBox === 3 ? 4 : 0,
      flex: 1,
      paddingLeft: 2,
      py: 1,
      paddingRight: "10px",
      borderColor: "divider",
      transition: "all 0.3s ease",
      cursor: "pointer",
      bgcolor: "white",
      "&:hover": {
        bgcolor: activeBox === 3 ? "white" : "grey.100",
      },
      "&:not(:hover)": {
        bgcolor: activeBox !== null && activeBox !== 3 ? "#EBEBEB" : "white",
      },
      "@media (max-width: 600px)": {
        px: 1,
        py: 0.8,
      },
    },
    searchButtonStyle: {
      fontSize: "18px",
      borderRadius: "32px",
      bgcolor: "primary.main",
      color: "white",
      ml: 2,
      minWidth: "0",
      "@media (max-width: 600px)": {
        fontSize: "14px",
        ml: 1,
      },
    },
    ResetButtonStyle: {
      fontSize: "18px",
      borderRadius: "32px",
      bgcolor: "primary.main",
      color: "white",
      minWidth: "0",
      "@media (max-width: 600px)": {
        fontSize: "14px",
        ml: 1,
      },
    },
    ratingBox: {
      paddingLeft: 1,
      // mt: 0.1,
      "& .MuiRating-icon": {
        fontSize: "1.3rem",
        color: "#FFB400",
      },
      "& .MuiRating-iconEmpty": {
        color: "#CBD5E1",
      },
      "@media (max-width: 600px)": {
        "& .MuiRating-icon": {
          fontSize: "0.8715rem",
        },
      },
    },

    dateTimeOverwrte: {
      "&.MuiFormControl-root .MuiInputBase-root": {
        fontSize: "0.875rem",
        paddingRight: 0,
        borderRadius: 0,
      },
      "&.MuiFormControl-root .MuiInputBase-root .MuiInputAdornment-root .MuiIconButton-root":
        {
          padding: "0px",
        },
      "&.MuiFormControl-root .MuiInputBase-root .MuiInputAdornment-root .MuiIconButton-root .MuiSvgIcon-root":
        {
          // display: "none",
          fontSize: "1.25rem",
          marginRight: 2,
        },
      "&.MuiFormControl-root .MuiInputBase-root .MuiInputBase-input": {
        padding: "0px",
      },
      "&.MuiFormControl-root .MuiInputBase-root .MuiOutlinedInput-notchedOutline":
        {
          border: "none",
        },
    },
  };

  return (
    <Paper ref={searchBarRef} elevation={1} sx={{ ...style.mainBox }}>
      <Box onClick={() => setActiveBox(0)} sx={{ ...style.childBox1 }}>
        <Box sx={{ ...style.childBox1Sibling }}>
          <Typography
            variant="caption"
            fontFamily="Poppins, sans-serif"
            fontWeight={600}
            lineHeight={0}
            sx={{
              "@media (max-width: 600px)": {
                fontSize: "0.65rem",
              },
            }}
          >
            {t("search")} {t("worker")}
          </Typography>
          <InputBase
            fullWidth
            type="text"
            placeholder={`${t("search")} ${t("worker")}`}
            value={searchKeys.workerName}
            onChange={(e) => {
              dispatch(
                setSearchKeys({ ...searchKeys, workerName: e.target.value })
              );
            }}
            sx={{
              fontSize: "0.875rem",
            }}
          />
        </Box>
      </Box>

      <Box onClick={() => setActiveBox(1)} sx={{ ...style.childBox2 }}>
        <Box sx={{ ...style.childBox2Sibling }}>
          <Typography variant="caption" fontWeight={600}>
            {t("location")}
          </Typography>
          <InputBase
            fullWidth
            value={searchKeys?.workerLocation?.addres || ""}
            onChange={handleInputChange}
            placeholder={`${t("search")} ${t("location")}`}
            sx={{ fontSize: "0.875rem", width: "90%" }}
          />

          {suggestions.length > 0 && (
            <Paper elevation={3} sx={{ ...style.suggestedLocationMenu }}>
              <List>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <ListItemText primary={suggestion.description} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Box>

      <Box onClick={() => setActiveBox(2)} sx={{ ...style.childBox3 }}>
        <Box sx={{ ...style.childBox3Sibling }}>
          <Typography variant="caption" fontWeight={600}>
            {t("dateAndTime")}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              disablePast
              // disableOpenPicker
              ampm={false}
              format="YYYY/MMM/DD"
              // open={activeBox === 2}
              value={searchKeys.workerDateTime || null}
              onChange={(newValue) => {
                dispatch(
                  setSearchKeys({ ...searchKeys, workerDateTime: newValue })
                );
                setActiveBox(null);
              }}
              // onClose={() => setActiveBox(null)}
              // onAccept={() => setActiveBox(null)}
              sx={{ ...style.dateTimeOverwrte }}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <Box onClick={() => setActiveBox(3)} sx={{ ...style.childBox4 }}>
        <Box display="flex" flexDirection="column" marginTop="2px">
          <Typography
            variant="caption"
            fontFamily="Poppins, sans-serif"
            fontWeight={600}
            paddingLeft={2}
            sx={{
              "@media (max-width: 600px)": {
                fontSize: "0.65rem",
              },
            }}
          >
            Rating
          </Typography>
          <Rating
            name="worker-rating"
            precision={0.5}
            value={searchKeys.workerRating}
            onChange={(event, newValue) => {
              dispatch(
                setSearchKeys({ ...searchKeys, workerRating: newValue })
              );
            }}
            disabled={activeBox !== 3}
            sx={{ ...style.ratingBox }}
          />
        </Box>
        <Button
          sx={{ ...style.searchButtonStyle }}
          onClick={handleSearchWorker}
        >
          <SearchIcon sx={{ fontSize: 30 }} />
        </Button>
      </Box>
    </Paper>
  );
};

export default SearchBar;
